/**
 * 路径展平（M3-15）。三次/二次贝塞尔 → 折线，供栅格化与选区转换共用。
 *
 * 用**固定步数细分**而不是自适应细分（按曲率動態加密）：钢笔路径通常不大，
 * 固定 32 段的误差早已远低于亚像素，自适应细分带来的实现复杂度在这个
 * 尺度上不划算——真的画超大曲线时按需再引入自适应版本。
 */
import type { Point } from './anchor';
import { parseCommands } from './anchor';

const SEGMENTS_PER_CURVE = 32;

function cubicPoint(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function quadPoint(p0: Point, p1: Point, p2: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

export interface FlattenedSubpath {
  readonly points: readonly Point[];
  readonly closed: boolean;
}

/** 把 SVG path 子集（M/L/C/Q/Z）展平为一组折线子路径。 */
export function flattenPath(commands: string): FlattenedSubpath[] {
  const { segments } = parseCommands(commands);
  const subpaths: FlattenedSubpath[] = [];
  let current: Point[] = [];
  let start: Point | null = null;
  let cursor: Point = { x: 0, y: 0 };
  let closed = false;

  const flush = (): void => {
    if (current.length > 0) subpaths.push({ points: current, closed });
    current = [];
    closed = false;
  };

  for (const seg of segments) {
    if (seg.kind === 'M') {
      flush();
      cursor = seg.to;
      start = seg.to;
      current = [cursor];
    } else if (seg.kind === 'L') {
      current.push(seg.to);
      cursor = seg.to;
    } else if (seg.kind === 'C') {
      for (let s = 1; s <= SEGMENTS_PER_CURVE; s++) {
        current.push(cubicPoint(cursor, seg.c1, seg.c2, seg.to, s / SEGMENTS_PER_CURVE));
      }
      cursor = seg.to;
    } else if (seg.kind === 'Q') {
      for (let s = 1; s <= SEGMENTS_PER_CURVE; s++) {
        current.push(quadPoint(cursor, seg.c, seg.to, s / SEGMENTS_PER_CURVE));
      }
      cursor = seg.to;
    } else {
      // Z：闭合回起点
      if (start !== null) current.push(start);
      closed = true;
      cursor = start ?? cursor;
    }
  }
  flush();
  return subpaths;
}
